import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Activity, Task } from "../../types/tasks";

type CalendarRange = "month" | "week" | "day";

interface EventItem {
  activity: Activity;
  task: Task;
}

interface Props {
  tasks: Task[];
  isAdmin: boolean;
  onEditTask: (task: Task) => void;
}

/** Returns the border-l color class for the activity card stripe. */
function activityCardStripe(a: Activity): string {
  if (a.completedAt) return "border-l-gray-400";
  if (!a.dueDate) return "border-l-gray-300";
  const today = format(new Date(), "yyyy-MM-dd");
  if (a.dueDate < today) return "border-l-blue-600"; // overdue → blue stripe
  if (a.dueDate === today) return "border-l-amber-500"; // today → amber stripe
  return "border-l-blue-400"; // upcoming → blue stripe
}

function parseHour(dueTime?: string): number | null {
  if (!dueTime) return null;
  const h = parseInt(dueTime.split(":")[0], 10);
  return isNaN(h) || h < 0 || h > 23 ? null : h;
}

const WEEKDAY_SHORT = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
] as const;
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_ROW_MIN_H = 52; // px — min-height so overlapping cards can expand the row
const TIME_COL_W = 52; // px
const MAX_MONTH_PILLS = 3;

export const TaskCalendar: React.FC<Props> = ({
  tasks,
  isAdmin,
  onEditTask,
}) => {
  const { t } = useTranslation("tasks");
  const [range, setRange] = useState<CalendarRange>("month");
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  const toggleDayExpand = (ds: string) =>
    setExpandedDays((prev) => {
      const next = new Set(prev);
      next.has(ds) ? next.delete(ds) : next.add(ds);
      return next;
    });

  // Scroll time-grid to 1 hour before current time when entering week/day view
  useEffect(() => {
    if ((range === "week" || range === "day") && scrollRef.current) {
      scrollRef.current.scrollTop =
        Math.max(0, new Date().getHours() - 1) * HOUR_ROW_MIN_H;
    }
  }, [range]);

  const navigate = (dir: -1 | 1) => {
    setCurrentDate((d) => {
      if (range === "month") return addMonths(d, dir);
      if (range === "week") return addWeeks(d, dir);
      return addDays(d, dir);
    });
  };

  const goToday = () => setCurrentDate(new Date());

  // ── View days ────────────────────────────────────────────────────────────
  const viewDays = useMemo<Date[]>(() => {
    if (range === "month") {
      const mStart = startOfMonth(currentDate);
      const mEnd = endOfMonth(currentDate);
      const gStart = startOfWeek(mStart, { weekStartsOn: 1 });
      const gEnd = endOfWeek(mEnd, { weekStartsOn: 1 });
      const result: Date[] = [];
      let d = gStart;
      while (d <= gEnd) {
        result.push(d);
        d = addDays(d, 1);
      }
      return result;
    }
    if (range === "week") {
      return eachDayOfInterval({
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
      });
    }
    return [currentDate];
  }, [range, currentDate]);

  // ── Event maps ───────────────────────────────────────────────────────────
  const { byDate, byDayHour, byDayAllDay } = useMemo(() => {
    const dateMap: Record<string, EventItem[]> = {};
    const hourMap: Record<string, EventItem[]> = {};
    const allDayMap: Record<string, EventItem[]> = {};

    const daySet = new Set(viewDays.map((d) => format(d, "yyyy-MM-dd")));

    tasks.forEach((task) => {
      task.activities.forEach((activity) => {
        if (!activity.dueDate) return;
        // month view — all dates
        if (!dateMap[activity.dueDate]) dateMap[activity.dueDate] = [];
        dateMap[activity.dueDate].push({ activity, task });
        // week/day view — only days in current view
        if (!daySet.has(activity.dueDate)) return;
        const h = parseHour(activity.dueTime);
        if (h !== null) {
          const key = `${activity.dueDate}:${h}`;
          if (!hourMap[key]) hourMap[key] = [];
          hourMap[key].push({ activity, task });
        } else {
          if (!allDayMap[activity.dueDate]) allDayMap[activity.dueDate] = [];
          allDayMap[activity.dueDate].push({ activity, task });
        }
      });
    });
    return { byDate: dateMap, byDayHour: hourMap, byDayAllDay: allDayMap };
  }, [viewDays, tasks]);

  // ── Header label ─────────────────────────────────────────────────────────
  const headerLabel = (() => {
    if (range === "month") return format(currentDate, "MMMM yyyy");
    if (range === "week") {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      const we = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(ws, "d MMM")} – ${format(we, "d MMM yyyy")}`;
    }
    return format(currentDate, "EEEE, d MMMM yyyy");
  })();

  // ── Activity card ─────────────────────────────────────────────────────────
  const renderActivityCard = ({ activity, task }: EventItem, key: string) => {
    const stripe = activityCardStripe(activity);
    return (
      <button
        key={key}
        onClick={() => onEditTask(task)}
        className={`w-full text-left rounded-sm border border-gray-200 border-l-4 ${stripe} bg-white px-1.5 py-1 hover:shadow-sm transition-shadow`}
      >
        {task.taskNumber && (
          <div className="text-[10px] text-gray-400 font-medium leading-none mb-0.5">
            {task.taskNumber}
          </div>
        )}
        <div className="text-xs font-semibold text-gray-800 leading-tight truncate">
          {activity.title}
        </div>
        {task.assigneeName && (
          <div className="text-[10px] text-gray-500 leading-none mt-0.5 truncate">
            {task.assigneeName}
          </div>
        )}
      </button>
    );
  };

  // ── Shared legend ────────────────────────────────────────────────────────
  const legend = (
    <div className="flex flex-wrap items-center gap-4 px-4 py-2.5 border-t border-gray-100 bg-gray-50">
      {(
        [
          { key: "upcoming", color: "bg-blue-400" },
          { key: "today", color: "bg-amber-500" },
          { key: "overdue", color: "bg-blue-600" },
          { key: "done", color: "bg-gray-400" },
        ] as const
      ).map(({ key, color }) => (
        <span
          key={key}
          className="flex items-center gap-1.5 text-xs text-gray-600"
        >
          <span className={`w-2 h-2 rounded-full inline-block ${color}`} />
          {t(`calendar.legend.${key}`)}
        </span>
      ))}
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <h2 className="text-sm font-semibold text-gray-900 text-center min-w-[180px]">
            {headerLabel}
          </h2>
          <button
            onClick={() => navigate(1)}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden text-xs">
            {(["month", "week", "day"] as CalendarRange[]).map((r, idx) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  idx > 0 ? "border-l border-gray-300" : ""
                } ${
                  range === r
                    ? "bg-primary text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {t(`calendar.range.${r}`)}
              </button>
            ))}
          </div>
          <button
            onClick={goToday}
            className="text-xs px-3 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {t("calendar.today")}
          </button>
        </div>
      </div>

      {/* ── MONTH VIEW ──────────────────────────────────────────────────────── */}
      {range === "month" && (
        <>
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
            {WEEKDAY_SHORT.map((wd) => (
              <div
                key={wd}
                className="py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide"
              >
                {wd}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {viewDays.map((day) => {
              const ds = format(day, "yyyy-MM-dd");
              const events = byDate[ds] ?? [];
              const inMonth = isSameMonth(day, currentDate);
              const isExpanded = expandedDays.has(ds);
              const overflow = events.length - MAX_MONTH_PILLS;
              const visible = isExpanded
                ? events
                : events.slice(0, MAX_MONTH_PILLS);

              return (
                <div
                  key={ds}
                  className={`border-r border-b border-gray-100 min-h-[96px] p-1.5 ${
                    !inMonth ? "bg-gray-50/80" : "bg-white"
                  }`}
                >
                  <div
                    className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full leading-none ${
                      isToday(day)
                        ? "bg-primary text-white"
                        : inMonth
                          ? "text-gray-800"
                          : "text-gray-400"
                    }`}
                  >
                    {format(day, "d")}
                  </div>
                  <div className="flex flex-col gap-1">
                    {visible.map((item) =>
                      renderActivityCard(item, item.activity.id),
                    )}
                    {!isExpanded && overflow > 0 && (
                      <button
                        onClick={() => toggleDayExpand(ds)}
                        className="text-[11px] text-primary font-medium px-1 text-left hover:underline"
                      >
                        +{overflow} {t("calendar.more")}
                      </button>
                    )}
                    {isExpanded && events.length > MAX_MONTH_PILLS && (
                      <button
                        onClick={() => toggleDayExpand(ds)}
                        className="text-[11px] text-gray-400 px-1 text-left hover:underline"
                      >
                        {t("calendar.showLess")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {legend}
        </>
      )}

      {/* ── WEEK / DAY VIEW (time-grid) ─────────────────────────────────────── */}
      {(range === "week" || range === "day") && (
        <>
          {/* Day column headers */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            {/* Time gutter spacer */}
            <div
              style={{ width: TIME_COL_W, minWidth: TIME_COL_W }}
              className="flex-shrink-0 border-r border-gray-200"
            />
            {viewDays.map((day) => {
              const ds = format(day, "yyyy-MM-dd");
              const today = isToday(day);
              return (
                <div
                  key={ds}
                  className={`flex-1 py-2 text-center border-r border-gray-100 last:border-r-0 ${
                    today ? "bg-primary/5" : ""
                  }`}
                >
                  <div
                    className={`text-xs font-semibold tracking-wide ${
                      today ? "text-primary" : "text-gray-500"
                    }`}
                  >
                    {format(day, range === "week" ? "EEE" : "EEEE")}
                  </div>
                  <div
                    className={`mx-auto mt-0.5 w-7 h-7 flex items-center justify-center rounded-full text-sm font-bold ${
                      today ? "bg-primary text-white" : "text-gray-700"
                    }`}
                  >
                    {format(day, "d")}
                  </div>
                </div>
              );
            })}
          </div>

          {/* All-day row */}
          <div className="flex border-b border-gray-200 min-h-[30px]">
            <div
              style={{ width: TIME_COL_W, minWidth: TIME_COL_W }}
              className="flex-shrink-0 border-r border-gray-200 flex items-center justify-end pr-2"
            >
              <span className="text-[10px] text-gray-400 uppercase font-medium tracking-wide">
                {t("gantt.allDay")}
              </span>
            </div>
            {viewDays.map((day) => {
              const ds = format(day, "yyyy-MM-dd");
              const events = byDayAllDay[ds] ?? [];
              return (
                <div
                  key={ds}
                  className={`flex-1 p-1 border-r border-gray-100 last:border-r-0 flex flex-col gap-1 ${
                    isToday(day) ? "bg-primary/5" : ""
                  }`}
                >
                  {events.map((item) =>
                    renderActivityCard(item, item.activity.id),
                  )}
                </div>
              );
            })}
          </div>

          {/* Scrollable 24-hour grid */}
          <div
            ref={scrollRef}
            className="overflow-y-auto"
            style={{ maxHeight: 500 }}
          >
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="flex"
                style={{ minHeight: HOUR_ROW_MIN_H }}
              >
                {/* Time label */}
                <div
                  style={{ width: TIME_COL_W, minWidth: TIME_COL_W }}
                  className="flex-shrink-0 border-r border-gray-200 flex items-start justify-end pr-2 pt-1"
                >
                  <span className="text-[10px] text-gray-400 leading-none tabular-nums">
                    {String(hour).padStart(2, "0")}:00
                  </span>
                </div>

                {/* Day cells */}
                {viewDays.map((day) => {
                  const ds = format(day, "yyyy-MM-dd");
                  const events = byDayHour[`${ds}:${hour}`] ?? [];
                  const today = isToday(day);
                  const isNowHour = today && new Date().getHours() === hour;
                  const minuteFraction = new Date().getMinutes() / 60;

                  return (
                    <div
                      key={ds}
                      className={`flex-1 border-r border-b border-gray-100 last:border-r-0 p-1 flex flex-col gap-1 relative ${
                        today ? "bg-primary/[0.02]" : ""
                      }`}
                    >
                      {/* Red current-time line — only in today's current-hour cell */}
                      {isNowHour && (
                        <div
                          style={{ top: `${minuteFraction * 100}%` }}
                          className="absolute left-0 right-0 h-px bg-red-400 z-10 pointer-events-none"
                        />
                      )}
                      {events.map((item) =>
                        renderActivityCard(item, item.activity.id),
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {legend}
        </>
      )}
    </div>
  );
};
